import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/core/lib/utils';
import { useUIStore } from '@/core/stores/ui.store';
import { useProjectFields } from '@/features/custom-fields/public';
import { TaskRow } from '../TaskListView/TaskRow';
import { InlineTaskRow } from '../TaskListView/InlineTaskRow';
import { ColumnSelectorPanel } from '../TaskListView/ColumnSelectorPanel';
import { useTaskDialogStore } from '../../hooks/useTaskDialogStore';
import type {
  Task,
  TaskColumnId,
  TaskPriority,
  CreateSubtaskPayload,
  UpdateSubtaskPayload,
} from '../../types';

const SUBTASK_DEFAULT_COLUMNS: TaskColumnId[] = [
  'title',
  'status',
  'priority',
  'assignee',
  'dueDate',
];

const COL_WIDTH: Partial<Record<TaskColumnId, string>> = {
  status: 'w-32',
  priority: 'w-24',
  assignee: 'w-32',
  dueDate: 'w-36',
  estimated: 'w-24',
  tags: 'w-48',
};

const COL_BREAKPOINT: Partial<Record<TaskColumnId, string>> = {
  status: 'hidden md:block',
  priority: 'hidden md:block',
  assignee: 'hidden md:block',
  dueDate: 'hidden lg:block',
  estimated: 'hidden lg:block',
  tags: 'hidden lg:block',
};

interface DialogSubtaskListProps {
  subtasks: Task[];
  isLoading: boolean;
  projectId: string;
  boardId: string;
  onCreate: (payload: CreateSubtaskPayload) => void;
  onUpdate: (id: string, patch: UpdateSubtaskPayload) => void;
  onDelete: (id: string) => void;
}

export function DialogSubtaskList({
  subtasks,
  isLoading,
  projectId,
  boardId,
  onCreate,
  onUpdate,
  onDelete,
}: DialogSubtaskListProps) {
  const { t } = useTranslation('tasks');
  const { pushTaskId } = useTaskDialogStore();
  const [isAdding, setIsAdding] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<TaskColumnId[]>(SUBTASK_DEFAULT_COLUMNS);

  const timeFormat = useUIStore((s) => s.timeFormat);
  const dateFormat = useUIStore((s) => s.dateFormat);
  const { data: fieldsResult } = useProjectFields(projectId);
  const fieldMap = Object.fromEntries((fieldsResult?.items ?? []).map((f) => [f.id ?? '', f]));

  const doneCount = subtasks.filter((s) => s.isCompleted).length;
  const activeColumns = visibleColumns.filter((c) => c !== 'title');

  return (
    <section data-testid="tasks-dialog-subtask-list">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium">
          {t('subtasks.title')}
          {subtasks.length > 0 && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              {t('subtasks.count', { done: doneCount, total: subtasks.length })}
            </span>
          )}
        </h3>
        <ColumnSelectorPanel
          visibleColumns={activeColumns}
          projectId={projectId}
          onColumnsChange={(cols) => setVisibleColumns(['title', ...cols])}
        />
      </div>

      {isLoading && subtasks.length === 0 && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      )}

      {!isLoading && subtasks.length === 0 && !isAdding && (
        <p className="py-3 text-center text-xs text-muted-foreground">{t('subtasks.empty')}</p>
      )}

      <div className="overflow-x-auto rounded-md border border-border/50">
        <div className="min-w-max">
          {/* Column header */}
          {(subtasks.length > 0 || isAdding) && (
            <div className="flex items-center gap-3 border-b border-border/50 bg-muted/30 text-xs font-medium text-muted-foreground">
              <div className="w-7 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="w-56 shrink-0 p-3 md:w-80">{t('listView.columns.title')}</div>
              {activeColumns.map((col) => (
                <div key={col} className={cn('shrink-0 p-3', COL_WIDTH[col], COL_BREAKPOINT[col])}>
                  {t(`listView.columns.${col}`)}
                </div>
              ))}
              <div className="w-8 shrink-0" />
            </div>
          )}

          {subtasks.map((subtask) => (
            <TaskRow
              key={subtask.id}
              task={subtask}
              boardId={boardId}
              visibleColumns={visibleColumns}
              columnWidths={{}}
              timeFormat={timeFormat}
              dateFormat={dateFormat}
              fieldMap={fieldMap}
              hideDragHandle
              hideCheckbox
              disableSubtasks
              depth={0}
              onOpenDetail={(id) => pushTaskId(id)}
              onEdit={(id) => pushTaskId(id)}
              onTitleSave={(id, title) => onUpdate(id, { name: title })}
              onStatusChange={(id, statusId) => onUpdate(id, { statusId })}
              onPriorityChange={(id, priority: TaskPriority | null) =>
                onUpdate(id, { priority: priority ?? undefined })
              }
              onAssigneeChange={(id, assigneeId) => onUpdate(id, { assigneeId })}
              onDateChange={(id, start, end) => onUpdate(id, { startDate: start, dueDate: end })}
              onEstimatedChange={(id, minutes) => onUpdate(id, { estimatedMinutes: minutes })}
              onDelete={(id) => onDelete(id)}
            />
          ))}

          {isAdding && (
            <InlineTaskRow
              visibleColumns={visibleColumns}
              columnWidths={{}}
              onConfirm={(title) => {
                onCreate({ name: title });
                setIsAdding(false);
              }}
              onCancel={() => setIsAdding(false)}
            />
          )}

          {!isAdding && (
            <button
              type="button"
              className="flex w-full items-center gap-2 px-4 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent/30 hover:text-foreground"
              onClick={() => setIsAdding(true)}
              aria-label={t('listView.addSubtask')}
            >
              <Plus className="size-3" />
              {t('listView.addSubtask')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
